import React, { useEffect, useState } from 'react';
import { BookOpen, Mic, ShieldCheck, UserCog, Users, Image as ImageIcon, Loader2 } from 'lucide-react';
import { listVoices, BrandVoiceProfile } from '@/lib/brandStudio';
import { GlassPanel } from './ui';

/**
 * Brand Guidelines — learned-data-driven tutorials & walkthroughs.
 * Pulls the latest trained brand voice (Analyze & Learn output) so the
 * "Zera brand voice" tab reflects what the AI actually learned.
 */

type GuideKey = 'stories' | 'voice' | 'rules' | 'admin' | 'user' | 'media';

interface Guide {
  key: GuideKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const GUIDES: Guide[] = [
  { key: 'stories', label: 'Posting Zera Stories', icon: BookOpen },
  { key: 'voice', label: 'Zera Brand Voice', icon: Mic },
  { key: 'rules', label: 'Social Posting Rules', icon: ShieldCheck },
  { key: 'admin', label: 'Admin Walkthrough', icon: UserCog },
  { key: 'user', label: 'User Walkthrough', icon: Users },
  { key: 'media', label: 'Media & Imagery', icon: ImageIcon },
];

const Step: React.FC<{ n: number; title: string; body: string }> = ({ n, title, body }) => (
  <div className="flex gap-4">
    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#C9A961]/15 text-xs font-medium text-[#E4C77F]">
      {n}
    </div>
    <div>
      <div className="text-sm font-medium text-white">{title}</div>
      <p className="mt-1 text-sm leading-relaxed text-white/55">{body}</p>
    </div>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
    {children}
  </span>
);

const BrandGuidelines: React.FC = () => {
  const [tab, setTab] = useState<GuideKey>('stories');
  const [voice, setVoice] = useState<BrandVoiceProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const v = await listVoices();
      setVoice(v[0] || null);
      setLoading(false);
    })();
  }, []);

  return (
    <GlassPanel className="overflow-hidden">
      <div className="border-b border-white/10 px-6 py-5">
        <div className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#C9A961]">
          Knowledge Base
        </div>
        <h3 className="mt-1 text-2xl font-light text-white">Zera Brand Guidelines</h3>
        <p className="mt-2 text-sm text-white/50">
          Tutorials and walkthroughs built from what the AI learned in Analyze &amp; Learn.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 px-6 py-4">
        {GUIDES.map((g) => {
          const Icon = g.icon;
          const active = tab === g.key;
          return (
            <button
              key={g.key}
              onClick={() => setTab(g.key)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all ${
                active
                  ? 'bg-white text-black'
                  : 'border border-white/15 text-white/60 hover:text-white'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {g.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-6 px-6 py-7">
        {tab === 'stories' && (
          <>
            <Step n={1} title="Lead with place, not product" body="Open every story with the destination or moment — a misty Albanian Alps morning, a Riviera sunset — before any offer." />
            <Step n={2} title="Three frames, one feeling" body="Keep stories to 3–5 frames: an establishing shot, a human detail, and a soft call to plan a journey." />
            <Step n={3} title="Native, unpolished texture" body="Stories can feel candid. Use natural light, minimal text overlays, and the Zera serif for any captions." />
            <Step n={4} title="Always close with intent" body="End with a quiet CTA sticker — 'Plan your journey' — linking to the booking flow, never a hard sell." />
          </>
        )}

        {tab === 'voice' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center gap-3 text-white/50">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading learned voice…
              </div>
            ) : voice ? (
              <>
                <p className="border-l-2 border-[#C9A961] pl-4 text-sm italic leading-relaxed text-white/70">
                  {voice.summary || 'Refined, evocative, and quietly luxurious — Zera speaks like a well-travelled confidant.'}
                </p>
                <div className="grid gap-6 md:grid-cols-2">
                  {voice.tone?.length > 0 && (
                    <div>
                      <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Tone</div>
                      <div className="flex flex-wrap gap-2">{voice.tone.map((t, i) => <Chip key={i}>{t}</Chip>)}</div>
                    </div>
                  )}
                  {voice.themes?.length > 0 && (
                    <div>
                      <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Themes</div>
                      <div className="flex flex-wrap gap-2">{voice.themes.map((t, i) => <Chip key={i}>{t}</Chip>)}</div>
                    </div>
                  )}
                  {voice.vocabulary?.length > 0 && (
                    <div>
                      <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Signature Vocabulary</div>
                      <div className="flex flex-wrap gap-2">{voice.vocabulary.map((t, i) => <Chip key={i}>{t}</Chip>)}</div>
                    </div>
                  )}
                  {voice.avoid_words?.length > 0 && (
                    <div>
                      <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Avoid</div>
                      <div className="flex flex-wrap gap-2">{voice.avoid_words.map((t, i) => <Chip key={i}>{t}</Chip>)}</div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-white/50">
                No trained brand voice yet. Run <strong className="text-white">Step 1 · Brand Voice Assistant</strong> to
                analyze your assets, and this tab will fill in automatically.
              </p>
            )}
          </div>
        )}

        {tab === 'rules' && (
          <>
            <Step n={1} title="One brand, one cadence" body="Post 4–7 times weekly with consistent golden-hour imagery. Never break the visual palette." />
            <Step n={2} title="Approval before publish" body="Every post passes through Admin Approval (Step 4). Nothing goes live without sign-off." />
            <Step n={3} title="Hashtags with restraint" body="8–12 curated hashtags max. Mix branded (#ZeraTravel) with destination and luxury-travel tags." />
            <Step n={4} title="No stock, no clichés" body="Avoid generic travel clichés and stock photography. Imagery must feel authored and exclusive." />
          </>
        )}

        {tab === 'admin' && (
          <>
            <Step n={1} title="Open Administration Suite" body="From the hub, choose Administration Suite. Authenticate once to receive a 6-digit access code." />
            <Step n={2} title="Review the approval queue" body="Posts sent from Step 3 appear here. Read caption, hashtags, theme and scheduled date." />
            <Step n={3} title="Approve, edit, or send back" body="Approve to advance a post to the Ready Library, or send it back to Step 3 with revision notes." />
            <Step n={4} title="Use the temporary code" body="The 6-digit code lets you re-enter the suite without retyping credentials during your session." />
          </>
        )}

        {tab === 'user' && (
          <>
            <Step n={1} title="Train the voice" body="Step 1 — upload assets or paste URLs, then Analyze & Learn to build the brand voice." />
            <Step n={2} title="Plan the content" body="Step 2 — pick a voice, number of days, and a post type per day, then generate the copy." />
            <Step n={3} title="Add media" body="Step 3 — for each post, upload media or send it to the Media Generation Bucket." />
            <Step n={4} title="Send for approval" body="Move posts to the approval bucket; once every post is handled, advance to Step 4." />
          </>
        )}

        {tab === 'media' && (
          <>
            <Step n={1} title="Golden-hour first" body="Imagery favours warm, low-angle light. Composition should feel cinematic and unhurried." />
            <Step n={2} title="People as accents" body="Show travellers small within grand landscapes — the place is always the hero." />
            <Step n={3} title="Consistent grade" body="Soft contrast, muted greens and gold highlights. Keep a cohesive grid aesthetic." />
            <Step n={4} title="Bucket, then generate" body="Choose AI media to queue posts into the Media Generation Bucket rather than generating instantly." />
          </>
        )}
      </div>
    </GlassPanel>
  );
};

export default BrandGuidelines;
